import { getRoot, Instance, types } from 'mobx-state-tree';
import { PageModel, RootType } from '../../../internal';
import { QuizApiItem, QuizQuestionApiItem } from '../../../utils/api/QuizApi';

export const PitanjaModel = types
  .compose(
    PageModel,
    types.model({
      quizzes: types.frozen<QuizApiItem[]>([]),
      selectedQuizId: types.maybe(types.number),
      questions: types.frozen<QuizQuestionApiItem[]>([]),
    })
  )
  .views((self) => ({
    get root(): RootType {
      return getRoot(self);
    },
    get selectedQuiz(): QuizApiItem | undefined {
      return self.quizzes.find((q) => q.id === self.selectedQuizId);
    },
  }))
  .actions((self) => ({
    setQuizzes(quizzes: QuizApiItem[]) {
      self.quizzes = quizzes;
    },
    setQuestions(questions: QuizQuestionApiItem[]) {
      self.questions = questions;
    },
    setSelectedQuizId(id: number) {
      self.selectedQuizId = id;
    },
    async beforePageEnter() {
      const quizzes = await self.root.api.quiz.getAll();
      this.setQuizzes(quizzes.slice().reverse());

      const { quizId } = self.root.router.queryParams;
      if (quizId) {
        this.setSelectedQuizId(Number(quizId));
        const questions = await self.root.api.quiz.getQuestions(Number(quizId));
        this.setQuestions(questions);
      }
    },
    async selectQuiz(quizId: number) {
      self.selectedQuizId = quizId;
      this.setQuestions([]);
      const questions = await self.root.api.quiz.getQuestions(quizId);
      this.setQuestions(questions);
      const view = self.root.router.views.get('Pitanja');
      self.root.router.history.replace(
        view.formatUrl({}, { quizId: String(quizId) })
      );
    },
    async deleteQuestion(questionId: number) {
      if (self.selectedQuizId == null) return;
      try {
        await self.root.api.quiz.deleteQuestion(
          self.selectedQuizId,
          questionId
        );
        this.setQuestions(self.questions.filter((q) => q.id !== questionId));
      } catch (err: any) {
        const message =
          err?.response?.data?.message ??
          'Došlo je do greške pri brisanju pitanja.';
        self.root.showFailure(message, null);
      }
    },
    async toggleQuestionActive(questionId: number, currentValue: boolean) {
      if (self.selectedQuizId == null) return;
      try {
        await self.root.api.quiz.updateQuestion(
          self.selectedQuizId,
          questionId,
          { isActive: !currentValue }
        );
        this.setQuestions(
          self.questions.map((q) =>
            q.id === questionId ? { ...q, isActive: !currentValue } : q
          )
        );
      } catch (err: any) {
        const message =
          err?.response?.data?.message ??
          'Došlo je do greške pri izmeni statusa pitanja.';
        self.root.showFailure(message, null);
      }
    },
  }))
  .named('PitanjaModel');

export type PitanjaModelType = Instance<typeof PitanjaModel>;
